import { withEllipsisMultiline } from "@utils/styles/common-styles";
import DeleteIcon from "@mui/icons-material/Delete";
import { AdmCompany, BaseMemberProps } from "@custom-types/base-types";
import { useEffect, useState } from "react";
import { ReactSetStateFunction } from "@utils/utility-types";
import { HelpIconTooltip } from "@components/common/help-icon-tooltip";
import { TextWithTooltip } from "@components/common/text-with-tooltip";
import {
  IconButton,
  Tooltip,
  Text,
  List,
  ListItem,
  ListItemText,
} from "@flat-ui";
import { DeleteMemberFromCompany } from "@components/delete-member/delete-member-from-company";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { MemberEvents } from "@utils/track-event/track-event-list";

interface Props extends BaseMemberProps {
  /** The companies associated with the member to be deleted */
  companies: AdmCompany[];

  /** Setter for shouldDisableContinueButton */
  setShouldDisableContinueButton: ReactSetStateFunction<boolean>;
}

/** Content for step 2 of delete member */
export function DeleteMemberStep2({
  companies,
  member,
  setShouldDisableContinueButton,
}: Props): React.JSX.Element {
  const [selectedCompany, setSelectedCompany] = useState<AdmCompany>();
  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    setShouldDisableContinueButton(companies.length > 0);
  }, [companies, setShouldDisableContinueButton]);

  return (
    <Text variant="bodyL">
      Before deleting this member, they must be removed from all workspaces. If
      they're the last Enterprise Admin or Group Manager of a workspace, delete
      the workspace first.
      <br />
      {companies.length === 0 ? (
        <Text variant="bodyL" sx={{ mt: 2 }}>
          This member is not associated with any workspaces.
        </Text>
      ) : (
        <>
          <Text variant="bodyL" sx={{ my: 2 }}>
            This member is associated with the following workspaces:
          </Text>

          <List dense={false} sx={{ maxHeight: 300, overflow: "auto" }}>
            {companies.map((company) => (
              <ListItem
                key={company.id}
                sx={{
                  px: "0px",

                  "& .MuiListItemText-root": {
                    ml: "12px",
                    ...withEllipsisMultiline,
                    maxWidth: "350px",
                  },
                }}
                secondaryAction={
                  <IconButton
                    onClick={() => {
                      trackEvent({
                        name: MemberEvents.openDeleteMemberFromWorkspaceDialog,
                        props: {
                          memberId: member.identity,
                          companyId: company.id,
                          location: "delete member step2",
                        },
                      });
                      company.memberCount > 1 && setSelectedCompany(company);
                    }}
                  >
                    {company.memberCount > 1 ? (
                      <Tooltip title="Remove member from workspace">
                        <DeleteIcon fontSize="small" />
                      </Tooltip>
                    ) : (
                      <HelpIconTooltip title="This member is the last Enterprise Admin or Group Manager of this workspace. Please delete the workspace first." />
                    )}
                  </IconButton>
                }
              >
                <ListItemText
                  primary={<TextWithTooltip text={company.name} />}
                  secondary={`ID: ${company.id}`}
                />
              </ListItem>
            ))}
          </List>
          {selectedCompany && (
            <DeleteMemberFromCompany
              member={member}
              company={selectedCompany}
              isOpen={!!selectedCompany}
              onClose={() => setSelectedCompany(undefined)}
              location="delete member step2"
            />
          )}
        </>
      )}
    </Text>
  );
}
